import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

/** Options de comparaison appliquées à la réponse saisie par l'élève. */
export interface TextMatchOptions {
  /** `false` : « Paris » et « paris » sont acceptés indifféremment. */
  caseSensitive: boolean;
  /** `true` : les espaces de début / fin sont ignorés. */
  trimWhitespace: boolean;
  /** `true` : normalise l'arabe (hamza, alif maqsura, tatweel, diacritiques). */
  normalizeArabic: boolean;
}

export interface TextAnswersValue extends TextMatchOptions {
  /** Liste des réponses acceptées (au moins une). */
  answers: string[];
}

export type TextAnswersForm = FormGroup<{
  answers: FormArray<FormControl<string>>;
  caseSensitive: FormControl<boolean>;
  trimWhitespace: FormControl<boolean>;
  normalizeArabic: FormControl<boolean>;
}>;

/**
 * Éditeur de réponses libres courtes (`short_text`).
 *
 * Présentationnel : le parent détient le sous-formulaire, ce composant se
 * contente de le rendre et de remonter les intentions d'ajout / suppression.
 */
@Component({
  selector: 'app-text-answers',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './text-answers.component.html',
  styleUrl: './text-answers.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextAnswersComponent {
  /** Sous-formulaire des réponses acceptées, construit par le parent. */
  @Input({ required: true }) group!: TextAnswersForm;

  /** Nombre minimum de réponses acceptées. */
  @Input() minAnswers = 1;

  /** Nombre maximum de réponses acceptées. */
  @Input() maxAnswers = 15;

  /** L'utilisateur demande une réponse acceptée supplémentaire. */
  @Output() addAnswer = new EventEmitter<void>();

  /** L'utilisateur demande la suppression de la réponse d'index donné. */
  @Output() removeAnswer = new EventEmitter<number>();

  get answers(): FormArray<FormControl<string>> {
    return this.group.controls.answers;
  }

  get canAdd(): boolean {
    return this.answers.length < this.maxAnswers;
  }

  get canRemove(): boolean {
    return this.answers.length > this.minAnswers;
  }
}
